/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action, get } from '@ember/object';
import DS from 'ember-data';
import { isEmpty } from '@ember/utils';
import { dropTask } from 'ember-concurrency';
import RouterService from '@ember/routing/router-service';
import EnvironmentService from 'cio/services/environment';
import ApiErrorsService from 'cio/services/api-errors';
import AnalyticsService from 'cio/services/analytics';
import WarehouseSyncModel, { createConfig } from 'cio/models/warehouse-sync';
import FormValidations from 'cio/utils/form-validations';
import { WarehouseValidations } from 'cio/utils/warehouse/validations';
import { FlyKeyboardEvent, FlyMouseEvent } from '@customerio/fly-components/types/fly-components/events';
import {
  IntegrationDestinationConfig,
  WarehouseDestination,
  WarehouseDestinationID,
  WarehouseStorageConfig,
  WarehouseStorageConfigKeys,
} from 'cio/utils/warehouse';
import ApiService from 'cio/services/api';
import AlertsService from 'cio/services/alerts';
import { capitalize } from '@ember/string';
import { IntegrationFields } from 'cio/utils/integrations/fields';

interface WarehouseSyncCreateArgs {
  destination: WarehouseDestinationID;
  onCreate: () => Promise<unknown>;
}

enum WarehouseSetupStep {
  Configure = 'configure',
  SelectData = 'select-data',
}

type WarehouseSetupStepDetail = {
  id: WarehouseSetupStep;
  label: string;
  disabled: boolean;
  complete: boolean;
  active: boolean;
};

export default class WarehouseSyncCreate extends Component<WarehouseSyncCreateArgs> {
  @service api!: ApiService;
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service analytics!: AnalyticsService;
  @service router!: RouterService;
  @service store!: DS.Store;
  @service environment!: EnvironmentService;

  readonly WarehouseSetupStep = WarehouseSetupStep;

  @tracked model: WarehouseSyncModel;
  @tracked configValidations!: FormValidations;
  @tracked activeStep = WarehouseSetupStep.Configure;
  @tracked hasConfirmedEnvironment = false;
  @tracked validationError = '';

  constructor(owner: unknown, args: WarehouseSyncCreateArgs) {
    super(owner, args);

    let { destination } = args;
    let modelConfigKey = IntegrationDestinationConfig[destination];
    let validationConfig = WarehouseValidations[destination];

    this.model = this.store.createRecord('warehouse-sync', {
      [modelConfigKey]: createConfig(destination),
    });

    this.configValidations = FormValidations.create({
      validations: validationConfig,
      context: this,
      modelKey: `model.${modelConfigKey}`,
    });
  }

  get nextButtonLabel(): string {
    if (this.activeStep === WarehouseSetupStep.Configure) {
      if (this.validateConfigAndNextTask.isRunning) {
        return 'Validating…';
      }

      return 'Validate & select data';
    } else {
      if (this.saveTask.isRunning) {
        return 'Creating…';
      }

      return 'Create and sync data';
    }
  }

  get destinationAzureBlob(): string {
    return WarehouseDestinationID.azure;
  }

  get destinationGcs(): string {
    return WarehouseDestinationID.gcs;
  }

  get destinationS3(): string {
    return WarehouseDestinationID.s3;
  }

  get destinationYandex(): string {
    return WarehouseDestinationID.yandex;
  }

  get steps(): WarehouseSetupStepDetail[] {
    return [
      {
        id: WarehouseSetupStep.Configure,
        label: 'Configure bucket',
        disabled: false,
        complete: this.hasRequiredConfig && !this.configValidations.hasErrors,
        active: this.activeStep === WarehouseSetupStep.Configure,
      },
      {
        id: WarehouseSetupStep.SelectData,
        label: 'Select data',
        disabled: this.configValidations.hasErrors,
        complete: this.hasConfirmedEnvironment,
        active: this.activeStep === WarehouseSetupStep.SelectData,
      },
    ];
  }

  get canGoToNextStep(): boolean {
    if (this.activeStep === WarehouseSetupStep.Configure) {
      return !this.configValidations.hasErrors;
    } else {
      return this.hasConfirmedEnvironment;
    }
  }

  @action
  async changeStep(step: WarehouseSetupStep): Promise<void> {
    if (this.activeStep === WarehouseSetupStep.Configure && !this.validateConfigAndNextTask.isRunning) {
      await this.validateConfigAndNextTask.perform();
    }
    this.activeStep = step;
  }

  @action
  async goToNextStep(e: FlyKeyboardEvent<HTMLFormElement> | FlyMouseEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();

    if (this.activeStep === WarehouseSetupStep.Configure) {
      await this.validateConfigAndNextTask.perform();
    } else {
      await this.saveTask.perform();
    }
  }

  @action
  goToPreviousStep(): void {
    if (this.activeStep === WarehouseSetupStep.Configure) {
      this.router.transitionTo('integrations.warehouse.type', { queryParams: { type: this.args.destination } });
    } else {
      this.changeStep(WarehouseSetupStep.Configure);
    }
  }

  saveTask = dropTask(async () => {
    this.apiErrors.clear();

    try {
      await this.model.save();
      await this.args.onCreate();
      this.router.transitionTo('integrations.warehouse.type', { queryParams: { type: this.args.destination } });
      this.alerts.success(
        'warehouse-sync.create',
        `Your data will now export to your ${this.model.bucketName} ${capitalize(this.model.type)} bucket`,
      );
      this.analytics.track('warehouse', 'create_sync_completed', { model: this.model });
    } catch (e) {
      this.apiErrors.report(e);
    }
  });

  validateConfigAndNextTask = dropTask(async () => {
    this.apiErrors.clear();

    await this.configValidations.validateModel();

    try {
      await this.api.post('validate_warehouse_config', {
        ignoreErrors: true,
        data: {
          warehouse_sync: this.model.serialize(),
        },
      });

      this.changeStep(WarehouseSetupStep.SelectData);
    } catch (e) {
      this.apiErrors.report(e);
    }
  });

  private get hasRequiredConfig(): boolean {
    return this.hasAllRequiredConfigForIntegration(this.args.destination);
  }

  private hasAllRequiredConfigForIntegration(destination: WarehouseDestination): boolean {
    let integrationTypeFields = get(IntegrationFields, destination) as WarehouseStorageConfigKeys[];
    let destinationModelKey = get(IntegrationDestinationConfig, destination);

    if (!integrationTypeFields || !destinationModelKey) {
      return false;
    }

    let model = this.model[destinationModelKey] as WarehouseStorageConfig;
    integrationTypeFields = integrationTypeFields.filter((field: string) => field !== 'bucketPath');
    return integrationTypeFields.every((key: never) => key && !isEmpty(model[key]));
  }
}
